import { buildAuthHeaders } from "../forum/authHeaders";
import { getCookie } from "../utils/csrf";

const IS_LOCAL = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE = IS_LOCAL
  ? String(process.env.REACT_APP_API_BASE_URL_LOCAL || "").replace(/\/+$/, "")
  : String(process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

function endpoint(path) {
  return API_BASE ? `${API_BASE}${path}` : path;
}

async function request(path, options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  const csrfToken = !["GET", "HEAD", "OPTIONS"].includes(method) ? getCookie("csrftoken") : "";
  const response = await fetch(endpoint(path), {
    ...options,
    credentials: "include",
    headers: buildAuthHeaders({
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
      ...(options.headers || {}),
    }),
  });
  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const details = payload?.detail || (payload && Object.values(payload).flat().filter((value) => typeof value === "string").join(" "));
    const error = new Error(details || "Le parcours Concierge est momentanément indisponible.");
    error.status = response.status;
    error.code = payload?.code || "concierge_request_failed";
    error.serverVersion = payload?.server_version;
    throw error;
  }
  return payload;
}

export function searchConciergeSport(filters, { signal } = {}) {
  return request("/api/concierge/sport/search/", {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(filters),
  });
}

export function fetchConciergeSportDraft({ signal } = {}) {
  return request("/api/concierge/sport/draft/", { signal });
}

export function saveConciergeSportDraft(values) {
  return request("/api/concierge/sport/draft/", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(values),
  });
}

export function archiveConciergeSportDraft() {
  return request("/api/concierge/sport/draft/", { method: "DELETE" });
}

export function fetchConciergeSportFollowUp(activityId, { signal } = {}) {
  const suffix = activityId ? `?activity_id=${encodeURIComponent(activityId)}` : "";
  return request(`/api/concierge/sport/follow-up/${suffix}`, { signal });
}

export function participationKey() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `concierge-sport:${window.crypto.randomUUID()}`;
  }
  return `concierge-sport:${Date.now()}:${Math.random().toString(36).slice(2)}:idem`;
}

export function fetchConciergeGroups({ signal } = {}) {
  return request("/api/concierge/sport/groups/", { signal });
}

export function fetchConciergeFriends({ signal } = {}) {
  return request("/api/concierge/sport/groups/friends/", { signal });
}

export function createConciergeGroup(values, key) {
  return request("/api/concierge/sport/groups/", { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": key }, body: JSON.stringify(values) });
}

export function actOnConciergeGroup(id, values) {
  return request(`/api/concierge/sport/groups/${encodeURIComponent(id)}/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(values) });
}

export function fetchConciergeDay(id, { signal } = {}) {
  return request(`/api/concierge/sport/activities/${encodeURIComponent(id)}/day/`, { signal });
}

export function actOnConciergeDay(id, values) {
  return request(`/api/concierge/sport/activities/${encodeURIComponent(id)}/day/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(values) });
}

export function saveConciergeDayEquipment(id, requiredEquipment) {
  return request(`/api/concierge/sport/activities/${encodeURIComponent(id)}/day/`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ required_equipment: requiredEquipment }) });
}

export function findConciergeReplacements(id, filters) {
  return request(`/api/concierge/sport/activities/${encodeURIComponent(id)}/alternatives/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(filters) });
}

export function participateConciergeSport(activityId, action, quantity = 1) {
  return request(`/api/concierge/sport/activities/${encodeURIComponent(activityId)}/participation/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": participationKey(),
    },
    body: JSON.stringify({ action, quantity }),
  });
}

export function recordConciergeMetric(event) {
  return request("/api/concierge/metrics/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event }),
  });
}
